// modules/heroCircles.js
// Concentric rings with pointer-repel. Multi-canvas, shared pointer per .team-hero__art

/* ───────── utils ───────── */
function parsePair(str, defX, defY) {
    const v = (str || '').split(',').map(s => parseFloat(s.trim()));
    return (v.length === 2 && v.every(Number.isFinite)) ? v : [defX, defY];
}
function cssNum(el, varName, fallback = 0) {
    const v = getComputedStyle(el).getPropertyValue(varName).trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
}
function dataNum(el, name, fallback = null) {
    const raw = el.dataset[name];
    if (raw == null) return fallback;
    const n = parseFloat(String(raw).trim());
    return Number.isFinite(n) ? n : fallback;
}
function dataInt(el, name, fallback = null) {
    const raw = el.dataset[name];
    if (raw == null) return fallback;
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) ? n : fallback;
}
function dataPair(el, name, defX, defY) {
    return parsePair(el.dataset[name] || '', defX, defY);
}

/* ───────── per-canvas setup ───────── */
function setupCanvas(canvas, sharedPointer) {
    if (canvas.__circlesCleanup) canvas.__circlesCleanup();

    const ctx  = canvas.getContext('2d');
    const art  = canvas.closest('.team-hero__art') || canvas.parentElement || canvas;
    const root = canvas.closest('.team-hero') || document.body;
    const divider = art.querySelector?.('.team-hero__divider-wrap');

    const state = {
        dpr: Math.max(1, Math.min(2, window.devicePixelRatio || 1)),
        rings: [],
        ringCount: dataInt(canvas, 'rings', 4),

        // influence radius (pointer)
        radiusMode: (canvas.dataset.radiusMode || 'px').toLowerCase(), // 'px' | 'min' | 'width'
        radiusBase: dataNum(canvas, 'radius', 160),
        radiusScale: dataNum(canvas, 'radiusScale', 0.55),
        maxRadius:  dataNum(canvas, 'maxRadius', Infinity),
        rSm: dataNum(canvas, 'radiusSm', null),
        rMd: dataNum(canvas, 'radiusMd', null),
        rLg: dataNum(canvas, 'radiusLg', null),
        rXl: dataNum(canvas, 'radiusXl', null),

        // ring set diameter (outer circle)
        sizeMode: (canvas.dataset.sizeMode || 'px').toLowerCase(), // 'px' | 'width' | 'min'
        sizePx:   dataNum(canvas, 'sizePx', null),
        sSm: dataNum(canvas, 'sizePxSm', null),
        sMd: dataNum(canvas, 'sizePxMd', null),
        sLg: dataNum(canvas, 'sizePxLg', null),
        sXl: dataNum(canvas, 'sizePxXl', null),
        sizeScale: dataNum(canvas, 'sizeScale', 0.30),

        // center (relative), with bp overrides
        centerRel:   dataPair(canvas, 'center', 0.82, 0.50),
        centerRelSm: dataPair(canvas, 'centerSm', NaN, NaN),
        centerRelMd: dataPair(canvas, 'centerMd', NaN, NaN),
        centerRelLg: dataPair(canvas, 'centerLg', NaN, NaN),
        centerRelXl: dataPair(canvas, 'centerXl', NaN, NaN),

        // ring geometry list
        radiiMode: (canvas.dataset.radiiMode || 'px').toLowerCase(), // 'px' | 'scale' | 'svg'
        radiiStr: canvas.dataset.radii || '',

        // drawing
        lineWidth: dataNum(canvas, 'lineWidth', 0.9),
        stroke: canvas.dataset.stroke || 'rgba(255,255,255,0.2)',
        segments: dataInt(canvas, 'segments', 220), // ↑ більше сегментів — плавніше

        // smoothing & physics
        strength: parseFloat(canvas.dataset.strength || '0.22'),
        smooth:   parseFloat(canvas.dataset.smooth   || '0.18'),

        // extra canvas bleed to avoid clipping on hover
        bleed: dataInt(canvas, 'bleed', 16),

        // [dock] докинг по краям для разных bp
        dock:    (canvas.dataset.dock    || '').toLowerCase(),   // left|right (и опционально top|bottom в будущем)
        dockSm:  (canvas.dataset.dockSm  || '').toLowerCase(),
        dockMd:  (canvas.dataset.dockMd  || '').toLowerCase(),
        dockLg:  (canvas.dataset.dockLg  || '').toLowerCase(),
        dockXl:  (canvas.dataset.dockXl  || '').toLowerCase(),

        shared: sharedPointer || { gx: 0, gy: 0, active: false },



        // derived each frame
        raf: 0,
        maxR: 1,
        center: { x: 0, y: 0 },
        radius: 160,
    };
    function dockForBp() {
        return pickBp(state.dockSm, state.dockMd, state.dockLg, state.dockXl, state.dock);
    }

    /* breakpoints */
    function bp() {
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        if (vw < 744) return 'sm';
        if (vw < 1024) return 'md';
        if (vw < 1280) return 'lg';
        return 'xl';
    }
    function pickBp(sm, md, lg, xl, base) {
        switch (bp()) {
            case 'sm': return sm ?? base;
            case 'md': return md ?? base;
            case 'lg': return lg ?? base;
            default:   return xl ?? base;
        }
    }
    function centerForBp() {
        const c = pickBp(state.centerRelSm, state.centerRelMd, state.centerRelLg, state.centerRelXl, state.centerRel);
        if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) return state.centerRel;
        return c;
    }

    /* compute outer-ring max radius (from diameter settings) */
    function calcMaxR(w, h) {
        const mode = state.sizeMode;
        if (mode === 'px') {
            const px = pickBp(state.sSm, state.sMd, state.sLg, state.sXl, state.sizePx); // DIAMETER
            if (px != null) return Math.max(1, Math.min(px, Math.max(w, h))) * 0.5;
            return Math.min(w, h) * 0.5;
        }
        const k = Math.max(0.01, state.sizeScale || 0.30);
        if (mode === 'width') return w * k * 0.5;
        if (mode === 'min')   return Math.min(w, h) * k * 0.5;
        return Math.min(w, h) * 0.5;
    }

    /* compute pointer influence radius */
    function calcInfluenceR(w, h) {
        const ov = pickBp(state.rSm, state.rMd, state.rLg, state.rXl, state.radiusBase);
        if (ov != null) return Math.max(1, Math.min(ov, state.maxRadius));
        if (state.radiusMode === 'width') return Math.max(1, Math.min(w * state.radiusScale, state.maxRadius));
        if (state.radiusMode === 'min')   return Math.max(1, Math.min(Math.min(w, h) * state.radiusScale, state.maxRadius));
        return Math.max(1, Math.min(state.radiusBase, state.maxRadius));
    }

    /* build rings list */
    function build() {
        const maxR = state.maxR;
        const count = Math.max(1, state.ringCount);
        const segments = Math.max(32, state.segments);

        let radii = [];
        if (state.radiiStr) {
            const src = state.radiiStr
                .split(',').map(s => parseFloat(s.trim()))
                .filter(n => Number.isFinite(n) && n > 0);

            if (state.radiiMode === 'scale') {
                radii = src.map(v => v * maxR); // fractions 0..1 of maxR
            } else if (state.radiiMode === 'svg') {
                const maxSvg = Math.max(...src) || 1;
                const k = maxR / maxSvg;
                radii = src.map(v => v * k);
            } else {
                // px
                radii = src.map(v => Math.min(v, maxR));
            }
        } else {
            // evenly spaced
            const s = maxR / count;
            for (let k = 1; k <= count; k++) radii.push(s * k);
        }

        state.rings = radii.map(r => {
            const nodes = [];
            for (let i = 0; i < segments; i++) {
                const t = (i / segments) * Math.PI * 2;
                const x0 = state.center.x + Math.cos(t) * r;
                const y0 = state.center.y + Math.sin(t) * r;
                nodes.push({ x0, y0, x: x0, y: y0 });
            }
            return { r, nodes };
        });
    }

    /* resize — compact canvas (just around the circle) with bleed */
    const ro = new ResizeObserver(resize);
    ro.observe(art);
    if (root !== art) ro.observe(root);
    window.addEventListener('orientationchange', resize, { passive: true });
    window.addEventListener('resize', resize, { passive: true });

    function resize() {
        // size of whole hero area
        let { width: W, height: H } = art.getBoundingClientRect();
        if (!W || !H) {
            const rb = root.getBoundingClientRect();
            const headerH = cssNum(root, '--header-h', 80);
            const offset  = cssNum(root, '--canvas-offset', 49);
            W = rb.width;
            H = Math.max(2, rb.height - headerH - offset);
        }
        W = Math.round(W); H = Math.round(H);

        // центр по относительным координатам (как было)
        const [cxr, cyr] = centerForBp();
        let heroCX = W * cxr;
        let heroCY = H * cyr;

        // вычисляем максимальный радиус и радиус влияния (как было)
        state.maxR  = calcMaxR(W, H);
        state.radius = calcInfluenceR(W, H);

        // [dock] если задан докинг — переопределяем центр по оси X,
        // чтобы правая (или левая) кромка канваса совпала с краем героя.
        const dock = dockForBp();
        if (dock === 'right') {
            heroCX = W - (state.maxR + state.bleed);
        } else if (dock === 'left') {
            heroCX = (state.maxR + state.bleed);
        }
        // при необходимости можно добавить:
        // if (dock === 'top')  heroCY = (state.maxR + state.bleed);
        // if (dock === 'bottom') heroCY = H - (state.maxR + state.bleed);

        // компактный размер канваса + bleed (как было)
        const diam = Math.max(2, Math.round(state.maxR * 2 + state.bleed * 2));
        canvas.width  = Math.floor(diam * state.dpr);
        canvas.height = Math.floor(diam * state.dpr);
        canvas.style.width  = diam + 'px';
        canvas.style.height = diam + 'px';

        // позиционирование (как было, но уже с учётом dock → heroCX)
        canvas.style.left = Math.round(heroCX - state.maxR - state.bleed) + 'px';
        canvas.style.top  = Math.round(heroCY - state.maxR - state.bleed) + 'px';

        // ... остальное без изменений ...
        ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
        state.center.x = state.maxR + state.bleed;
        state.center.y = state.maxR + state.bleed;

        if (divider) divider.style.top = Math.round(heroCY) + 'px';

        build();
    }

    /* animation */
    function step() {
        // map shared pointer to local coords each frame
        const rect = canvas.getBoundingClientRect();
        const pActive = !!state.shared.active;
        const pX = state.shared.gx - rect.left;
        const pY = state.shared.gy - rect.top;

        const maxR = state.maxR;

        for (const ring of state.rings) {
            const ringScale = ring.r / maxR;
            for (let i = 0; i < ring.nodes.length; i++) {
                const n = ring.nodes[i];
                let tx = n.x0, ty = n.y0;

                if (pActive) {
                    const dx = n.x0 - pX, dy = n.y0 - pY;
                    const dist = Math.hypot(dx, dy) || 1e-4;
                    if (dist < state.radius) {
                        const fall = 1 - dist / state.radius;
                        const mag  = state.strength * ringScale * (fall * fall) * state.radius;
                        tx += (dx / dist) * mag;
                        ty += (dy / dist) * mag;
                    }
                }

                n.x += (tx - n.x) * state.smooth;
                n.y += (ty - n.y) * state.smooth;
            }
        }

        // draw
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        ctx.clearRect(0, 0, w, h);

        ctx.lineWidth = state.lineWidth;
        ctx.strokeStyle = state.stroke;
        ctx.lineJoin = 'round'; // ← плавні стики
        ctx.lineCap  = 'round'; // ← плавні кінці

        for (const ring of state.rings) {
            const nodes = ring.nodes;
            ctx.beginPath();
            ctx.moveTo(nodes[0].x, nodes[0].y);
            for (let i = 1; i < nodes.length; i++) ctx.lineTo(nodes[i].x, nodes[i].y);
            ctx.closePath();
            ctx.stroke();
        }

        state.raf = requestAnimationFrame(step);
    }

    /* activate */
    resize();
    state.raf = requestAnimationFrame(step);

    /* cleanup */
    canvas.__circlesCleanup = () => {
        cancelAnimationFrame(state.raf);
        ro.disconnect();
        window.removeEventListener('orientationchange', resize);
        window.removeEventListener('resize', resize);
        canvas.__circlesCleanup = null;
    };
}

/* ───────── entry ───────── */
export default function initHeroCircles(root = document) {
    // групуємо за .team-hero__art, щоб мати спільний курсор для набору canvas
    root.querySelectorAll('.team-hero__art').forEach(art => {
        const canvases = art.querySelectorAll('.circles-canvas[data-circles]');
        if (!canvases.length) return;

        const shared = { gx: 0, gy: 0, active: false };

        function onPointer(e) { shared.gx = e.clientX; shared.gy = e.clientY; shared.active = true; }
        function onLeave()    { shared.active = false; }

        art.addEventListener('pointermove', onPointer, { passive: true });
        art.addEventListener('pointerdown', onPointer, { passive: true });
        art.addEventListener('pointerenter', onPointer, { passive: true });
        art.addEventListener('pointerleave', onLeave,   { passive: true });

        canvases.forEach(c => setupCanvas(c, shared));
    });
}
